import { createSlice } from "@reduxjs/toolkit";
import _, { find, flatMap } from "lodash";
import { BookingJourneyStep } from "utils/types";
import { moveStepBackward, moveStepForward } from "./wizardHelper";
import { persistReducer } from "redux-persist";
import localStorage from "redux-persist/lib/storage";
import { BOOKING_JOURNEY } from "utils/constants";
import { NIL } from "uuid";

export interface Basket {
  products: ProductBasket[] | null;
  bookingId?: string;
  bookingNo?: string;
}
export enum ModificationStatus {
  None,
  Added,
  Deleted,
  Modified,
}

export enum POUploadStatus {
  None,
  Uploading,
  Success,
  InvalidFileType,
  FileTooLarge,
  VirusFailed,
  UploadFailed,
}
export interface ProductDetails {
  alternativeDates?: string;
  id: string;
  date?: string;
  dateType: string;
  deadlineDate?: string;
  fromDateRange?: string;
  preferredMonth?: string;
  toDateRange?: string;
  preferredDate?: string;
  preferredStartTime?: string;
  location?: string | null;
  roomNumber?: string | null;
  address?: string | null;
  modificationStatus?: ModificationStatus | null;
  hostAndDelegates?: HostAndDelegates | null;
  venueId: string;
  poNumber?: string;
  costCentre?: string;
  approvalId?: string;
  businessUnit?: string;
  country?: string;
  businessArea?: string;
  eventNotes?: string | null;
  poId?: string;
  bookingId?: string;
  eventId?: string;
  bookingNo?: string;
  dateEventRequested?: string;
  poUploadFileName?: string;
  poUploadFileLink?: string;
}

export interface HostAndDelegates {
  id: string;
  hostId?: string;
  hostFirstName: string;
  hostLastName: string;
  hostEmailAddress: string;
  hostPhoneNumber: string;
  areYouHost?: boolean;
  hasDelegates: boolean;
  modificationStatus?: ModificationStatus | null;
  delegateDetails?: DelegateDetail[] | null;
}

export interface DelegateDetail {
  id: string;
  delegateId?: string | null;
  firstName: string;
  lastName: string;
  emailAddress: string;
  isDeleted?: boolean;
  modificationStatus?: ModificationStatus | null;
}

export interface ProductBasket {
  id: string;
  courseId: string;
  title: string;
  image: string;
  bookingType: string;
  deliveryMethod: string;
  venue: string;
  maximumDelegates: number | null;
  minimumDelegates: number | null;
  workshopLength: number | null;
  quantity: number;
  price: number;
  subTotal: number;
  customerProductCode: string | null;
  productDetails: ProductDetails[] | null;
  modificationStatus?: ModificationStatus | null;
  activityId?: string;
  priceId?: string;
  currency?: string;
  eventCategory: string;
  bookingId?: string;
  isValid: boolean;
  eventStatus?: string;
  eventDate?: string;
}

export interface CompletionInfo {
  hasGroupBooking: boolean;
  hasInvidualBooking: boolean;
}

export interface BookingJourneyState {
  basket: Basket | null;
  currentStep: BookingJourneyStep;
  completionInfo: CompletionInfo | null;
}

const initialState: BookingJourneyState = {
  basket: { products: [] },
  currentStep: BookingJourneyStep.Summary,
  completionInfo: null,
};

const bookingJourneySlice = createSlice({
  name: "bookingJourney",
  initialState: initialState,
  reducers: {
    addToBasket: (state, param) => {
      state.basket = param.payload.basket;
      if (param?.payload?.moveForward) moveStepForward(state);
    },
    clearBasket: (state) => {
      const updatedBasket: Basket = { products: [], bookingId: null, bookingNo: null };
      state.basket = updatedBasket;
    },
    addOrUpdateToBasket: (state, param) => {
      if (param?.payload) {
        const existingProduct = _.find(state.basket.products, (p) => {
          return (
            p.courseId === param.payload.courseToBeAdded.courseId &&
            p.price === param.payload.courseToBeAdded.price &&
            p.priceId === param.payload.courseToBeAdded.priceId &&
            p.deliveryMethod === param.payload.courseToBeAdded.deliveryMethod &&
            p.bookingType === param.payload.courseToBeAdded.bookingType &&
            p.minimumDelegates === param.payload.courseToBeAdded.minimumDelegates &&
            p.maximumDelegates === param.payload.courseToBeAdded.maximumDelegates
          );
        });

        if (existingProduct) {
          existingProduct.quantity += param.payload.courseToBeAdded.quantity;
          existingProduct.subTotal += param.payload.courseToBeAdded.subTotal;
          const existingProductWithDetails = existingProduct.productDetails.filter(
            (x) => x.preferredStartTime != null,
          );
          existingProduct.productDetails = param.payload.courseToBeAdded.productDetails.filter(
            (x) => x.preferredStartTime === null,
          );
          existingProductWithDetails.forEach((detail) => {
            existingProduct.productDetails.push(detail);
          });
        } else {
          // If the product doesn't exist, add it to the basket
          state.basket.products.push(param.payload.courseToBeAdded);
        }
        state.basket.bookingId = param.payload.bookingId;
        state.basket.bookingNo = param.payload.bookingNo;
      }
    },
    updateBasket: (state, param) => {
      if (param?.payload) {
        const existingProduct = state.basket.products.find((p) => p.id === param.payload.id);
        if (existingProduct) {
          existingProduct.quantity += 1;
          existingProduct.subTotal += param.payload.price;
          existingProduct.productDetails.push({
            id: NIL,
            date: "",
            dateType: "",
            preferredStartTime: "",
            location: "",
            roomNumber: "",
            address: "",
            alternativeDates: null,
            modificationStatus: ModificationStatus.Added,
            venueId: NIL,
          });
        }
      }
    },
    removeFromBasket: (state, param) => {
      if (param?.payload) {
        const existingProduct = state.basket.products.find((p) => p.id === param.payload.id);
        if (existingProduct) {
          if (param.payload.quantity === 1) {
            //if current quantity is 1 then reducing quantity should mark full product as deleted
            existingProduct.modificationStatus = ModificationStatus.Deleted;
          } else {
            existingProduct.quantity -= 1;
            existingProduct.subTotal -= existingProduct.price;
            //find the last element of productdetails and mark as deleted,if it is already marked as dleted then take the next one
            const lastDetail = existingProduct.productDetails
              .filter((p) => p.modificationStatus !== ModificationStatus.Deleted)
              .slice(-1)[0];
            if (lastDetail) lastDetail.modificationStatus = ModificationStatus.Deleted;

            // Filter out productDetails with blank IDs
            existingProduct.productDetails = existingProduct.productDetails.filter(
              (p) => !(p.id === NIL && p.modificationStatus === ModificationStatus.Deleted),
            );
          }
        }
      }
    },
    deleteFromBasket: (state, param) => {
      if (param?.payload) {
        const { productId, productDetailId } = param.payload;
        // Find the product by ID
        const product = state.basket.products.find((product) => product.id === productId);

        if (!product) return;

        if (productDetailId) {
          // Filter out the productDetail by ID
          product.productDetails = product.productDetails.filter(
            (detail) => detail.id !== productDetailId,
          );
          product.quantity -= 1;
          product.subTotal -= product.price;

          // Remove the product if no details are left
          if (product.productDetails.length === 0) {
            state.basket.products = state.basket.products.filter((p) => p.id !== productId);
          }
        } else {
          // Remove the entire product if no productDetailId is provided
          state.basket.products = state.basket.products.filter((p) => p.id !== productId);
        }
      }
    },
    addOrUpdateDateLocation: (state, param) => {
      if (!param?.payload) return;

      const product = state.basket.products.find((p) => p.id === param.payload.productId);
      if (product) {
        product.productDetails = param.payload.productDetails;
      }
    },

    addOrUpdateHostDetail: (state, param) => {
      if (param?.payload) {
        const product = find(flatMap(state.basket.products, "productDetails"), {
          id: param.payload.detailId,
        });

        if (product.hostAndDelegates) {
          product.hostAndDelegates[param.payload.fieldName] = param.payload.value;
        } else {
          product.hostAndDelegates = param.payload.hostAndDelegates;
        }

        product.hostAndDelegates.modificationStatus = ModificationStatus.Modified;

        if (param.payload.fieldName === "hasDelegates") {
          product.hostAndDelegates.delegateDetails?.map((del: DelegateDetail) => {
            return (del.modificationStatus =
              param.payload.value === param.payload.isPublicBooking
                ? ModificationStatus.Deleted
                : ModificationStatus.Modified);
          });
        }
      }
    },

    addUpdateOrDeleteDelegateDetails: (state, param) => {
      if (param?.payload) {
        const product = find(flatMap(state.basket.products, "productDetails"), {
          id: param.payload.detailId,
        });

        if (!product?.hostAndDelegates) return;

        const delegate = find(product.hostAndDelegates.delegateDetails, {
          id: param.payload.delegateId,
        });
        if (delegate) {
          delegate[param.payload.fieldName] = param.payload.value;
          delegate.modificationStatus = param.payload.delegate.isDeleted
            ? ModificationStatus.Deleted
            : ModificationStatus.Modified;
        } else {
          if (!product.hostAndDelegates.delegateDetails) {
            product.hostAndDelegates.delegateDetails = [];
          }
          product.hostAndDelegates.delegateDetails.push(param.payload.delegate);
        }
        product.hostAndDelegates.modificationStatus = ModificationStatus.Modified;
      }
    },

    addOrUpdatePaymentDetails: (state, param) => {
      if (param?.payload) {
        const product = find(flatMap(state.basket.products, "productDetails"), {
          id: param.payload.detailId,
        });
        product[param.payload.fieldName] = param.payload.value;
        product.modificationStatus = ModificationStatus.Modified;
      }
    },

    updatePOUploadState: (state, param) => {
      if (param?.payload) {
        const detail = find(flatMap(state.basket.products, "productDetails"), {
          id: param.payload.detailId,
        });
        if (detail) {
          if (param.payload.fileName !== undefined) {
            detail.poUploadFileName = param.payload.fileName;
          }
          if (param.payload.fileLink !== undefined) {
            detail.poUploadFileLink = param.payload.fileLink;
          }
          if (param.payload.poId !== undefined) {
            detail.poId = param.payload.poId;
          }
          detail.modificationStatus = ModificationStatus.Modified;
        }
      }
    },

    updateProductDetailField: (state, param) => {
      if (param?.payload) {
        const product = find(flatMap(state.basket.products, "productDetails"), {
          id: param.payload.detailId,
        });
        product[param.payload.fieldName] = param.payload.value;
      }
    },

    goToStep: (state, param) => {
      return { ...state, currentStep: param?.payload };
    },
    moveForward: (state) => {
      moveStepForward(state);
    },
    moveBackward: (state) => {
      moveStepBackward(state);
    },
    setCompletionInfo: (state, param) => {
      state.completionInfo = param.payload;
    },
    clearCompletionInfo: (state) => {
      state.completionInfo = null;
    },
  },
});

const { actions, reducer } = bookingJourneySlice;
export const {
  addToBasket,
  addOrUpdateToBasket,
  updateBasket,
  removeFromBasket,
  deleteFromBasket,
  addOrUpdateDateLocation,
  addUpdateOrDeleteDelegateDetails,
  addOrUpdateHostDetail,
  addOrUpdatePaymentDetails,
  updatePOUploadState,
  updateProductDetailField,
  clearBasket,
  goToStep,
  moveForward,
  moveBackward,
  setCompletionInfo,
  clearCompletionInfo,
} = actions;

export default reducer;

const bookerJourneyPersistConfig = {
  key: BOOKING_JOURNEY,
  storage: localStorage,
};

export const persistedBookingJourneyReducer = persistReducer(
  bookerJourneyPersistConfig,
  bookingJourneySlice.reducer,
);
